import { useId, useState } from "react";
import { useNavigate } from "react-router";

import { ApiError } from "@/api/client";
import { queryKeys } from "@/api/hooks";
import {
  useAssetVersionQuery,
  useCreateLibraryAssetMutation,
  useLibraryAssetsQuery,
  useLibraryPlacementRegistryQuery,
  type LibraryAsset,
} from "@/api/library";
import { useRetryPausedQuery } from "@/api/useRetryPausedQuery";
import {
  isPartsContent,
  toDraft,
  toPartsContent,
  type DraftPart,
} from "@/lib/assetContent";
import {
  domainDocuments,
  setDomainId,
  storedDomainSummary,
} from "@/lib/backgroundDomains";
import { libraryAssetHref } from "@/lib/librarySections";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { FieldError, joinDescriptionIds } from "@/ui/FieldError";
import { Field, Input, Select } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import { createFieldErrors } from "@/ui/library/NewAssetDialog";
import { LoadingState } from "@/ui/states";

import { NamedReadFailureState } from "./NamedReadFailure";

/**
 * Create a BG domain, or an editable copy of one (#859).
 *
 * ## WHY THIS FAMILY GETS ITS OWN DIALOG
 *
 * The generic `NewAssetDialog` authors a v1 through `PartsEditor` — a free-text
 * "Part key" input over a raw JSON textarea — which the 2026-08-25 amendment
 * forbids and which the ratified contract lists among the rejected mechanisms.
 * `background_set` was simply absent from `CREATABLE_ASSET_TYPES` instead,
 * because before #857 an authored BG set had no runtime placement contract and
 * a Create control would have manufactured an asset no project could serialize.
 *
 * #857 supplied that contract: a background set's coordinate is
 * `<profile variant>/<domain id>`, its files land in the variant's own domains
 * directory, and `validate_background_domains` refuses a document whose
 * `domain` disagrees with the coordinate's slug. So creation is now possible
 * AND has exactly two facts a clinician must decide — which profile variant the
 * domain belongs to, and what the domain is called — which is what this dialog
 * asks for.
 *
 * ## THE COORDINATE IS ASSEMBLED, NOT TYPED
 *
 * `NewMainConcernDialog` and `NewPathwayDialog` both record a single divergence
 * from the reference: a raw `placementCoordinate` field, because the coordinate
 * is "explicit, never derived from the name, and immutable once stored". This
 * family's coordinate has TWO segments with two different meanings, and a
 * single text box holding `mod/military_service` would make a clinician
 * responsible for a separator whose meaning nothing on screen explains. So the
 * two segments are two fields and the coordinate is joined from them — which is
 * assembly of what the clinician entered, not derivation from the asset name.
 *
 * The SHAPE those fields must take is read from the placement registry
 * (`families[].placement.segmentPattern`), never restated here: the service
 * publishes the pattern precisely so a client can validate against the same
 * rule that refuses, and a second copy in the browser is the drift #300 spent a
 * ticket deleting. The number of segments is read the same way, so a family
 * whose arity changes fails this dialog's own guard rather than silently
 * building a coordinate the service will reject.
 *
 * ## STARTING CONTENT IS AN EXACT STORED VERSION
 *
 * The same rule `NewPathwayDialog` states: create publishes an immutable v1
 * that may be adopted immediately, so a placeholder document would make
 * placeholder content clinical content. Every new domain therefore starts from
 * an existing domain's exact version — which also means it starts with real
 * conditional rules, extraction notes and quality examples, and the clinician
 * edits from there.
 *
 * The ONE field this dialog rewrites in the copied document is `domain`,
 * because the service refuses a document whose id is not its coordinate's slug.
 * It is a surgical edit through `lib/backgroundDomains`, so everything else the
 * source version held survives into v1 unchanged.
 */

const NAME_MAX_LENGTH = 60;

const REFUSED_CONTENT =
  "This domain's content could not be prepared, so nothing was created.";

/** How many segments a background-set coordinate carries, per #857. */
const EXPECTED_SEGMENTS = 2;

export function NewBackgroundDomainDialog({
  onClose,
  copyFrom,
}: {
  onClose: () => void;
  /** Opens as "create an editable copy of this domain". */
  copyFrom?: LibraryAsset;
}) {
  const navigate = useNavigate();
  const create = useCreateLibraryAssetMutation();
  const catalog = useLibraryAssetsQuery(false);
  const registry = useLibraryPlacementRegistryQuery();
  const nameErrorId = useId();
  const variantErrorId = useId();
  const domainErrorId = useId();
  const placementErrorId = useId();

  const [name, setName] = useState(copyFrom ? `${copyFrom.name} (copy)` : "");
  const [sourceId, setSourceId] = useState(copyFrom?.id ?? "");
  const [variant, setVariant] = useState("");
  // NOT `setDomainId`: `lib/backgroundDomains` exports a function by that name
  // and the local setter would shadow it inside `submit`.
  const [domainId, changeDomainId] = useState("");
  const [blocked, setBlocked] = useState<string | null>(null);

  const assets = catalog.data?.items ?? [];
  const sourceCandidates = assets.filter(
    (candidate) => candidate.assetType === "background_set",
  );
  // A direct copy may start from an archived seeded asset, which is deliberately
  // absent from the active catalog; a normal New journey chooses an active row.
  const sourceAsset =
    (copyFrom?.id === sourceId ? copyFrom : null) ??
    sourceCandidates.find((candidate) => candidate.id === sourceId) ??
    null;

  const source = useAssetVersionQuery(
    sourceAsset?.id ?? "",
    sourceAsset ? sourceAsset.latestVersion : null,
  );
  const retrySource = useRetryPausedQuery(
    queryKeys.assetVersion(
      sourceAsset?.id ?? "",
      sourceAsset?.latestVersion ?? "",
    ),
  );
  const retryCatalog = useRetryPausedQuery(queryKeys.libraryAssets(false));
  const retryRegistry = useRetryPausedQuery(queryKeys.libraryPlacementRegistry);

  const placement = registry.data?.families.find(
    (family) => family.assetType === "background_set",
  )?.placement;
  // THE SERVICE'S OWN GRAMMAR, anchored in this dialect and nowhere restated.
  // The registry publishes the pattern UNANCHORED precisely so each client
  // anchors it in its own regex syntax.
  const segmentPattern = placement?.segmentPattern ?? null;
  const segmentRule =
    segmentPattern === null ? null : new RegExp(`^(?:${segmentPattern})$`);
  // A registry that ANSWERED and no longer describes a two-segment coordinate
  // for this family: the shape changed under this build, and a coordinate
  // assembled from two fields would be one the service never asked for.
  const policyUnusable =
    registry.isSuccess &&
    (placement?.kind !== "coordinate" ||
      placement.coordinateSegments !== EXPECTED_SEGMENTS);

  // AN UNREAD POLICY IS NOT A PERMISSIVE ONE. `segmentRule` is null while the
  // registry is pending or has failed, and a null rule makes every segment
  // "valid" — so without the registry among the prerequisites below, a dialog
  // whose policy read never arrived would validate nothing, enable Create, and
  // submit a coordinate against a grammar it had not read. `policyUnusable`
  // cannot cover that on its own: it is deliberately scoped to a SUCCESSFUL
  // read, because "the family's shape is wrong" and "we do not know the
  // family's shape" are different facts with different remedies.
  //
  // The same ruling `BgSetAdoptionPicker` applies to its own registry read
  // (`POLICY_UNREAD_SENTENCE`) and `MainConcernAssetPage` to its part manifest:
  // a client whose last read of service policy failed may not act on that
  // policy, cached or not.
  const variantValid = segmentRule === null || segmentRule.test(variant);
  const domainValid = segmentRule === null || segmentRule.test(domainId);
  const coordinate = `${variant}/${domainId}`;

  const fieldErrors = create.isError ? createFieldErrors(create.error) : {};
  const generalMessage =
    blocked ??
    (create.isError && Object.keys(fieldErrors).length === 0
      ? create.error instanceof ApiError
        ? create.error.message
        : "The Editor Service could not be reached, so nothing was created."
      : null);

  const busy = create.isPending;
  const sourceUnavailable =
    !copyFrom && sourceId !== "" && catalog.isSuccess && sourceAsset === null;

  function submit() {
    setBlocked(null);

    const stored = source.isSuccess ? source.data.content : null;
    if (sourceAsset === null || stored === null || !isPartsContent(stored)) {
      setBlocked(
        "The Editor Service did not return usable starting content for this domain, so nothing was created.",
      );
      return;
    }

    const parts: DraftPart[] = toDraft(stored);
    if (domainDocuments(parts).length === 0) {
      // The source version carries no domain document at all, so there is
      // nothing this dialog could re-identify. Refused rather than submitted:
      // the service would answer the same, and this sentence names the cause.
      setBlocked(
        "The chosen starting content carries no background domain document, so nothing was created.",
      );
      return;
    }
    // THE ONE REWRITE. Every language document must name the new domain, since
    // `validate_background_domains` checks each one against the coordinate's
    // slug — and each is otherwise carried through exactly as stored.
    const content = toPartsContent(setDomainId(parts, domainId));
    if (!content.ok) {
      setBlocked(content.problems[0]?.message ?? REFUSED_CONTENT);
      return;
    }

    create.mutate(
      {
        assetType: "background_set",
        name: name.trim(),
        content: content.content,
        note: `Started from "${sourceAsset.name}" at ${sourceAsset.latestVersion}.`,
        placementCoordinate: coordinate,
      },
      {
        onSuccess(created) {
          onClose();
          void navigate(libraryAssetHref(created.assetType, created.id));
        },
      },
    );
  }

  const catalogFailure =
    catalog.isPaused || catalog.isError
      ? {
          failure: catalog.isPaused ? catalog.failureReason : catalog.error,
          isPaused: catalog.isPaused,
          retry: retryCatalog,
        }
      : null;
  const sourceFailure =
    sourceAsset !== null && (source.isPaused || source.isError)
      ? {
          failure: source.isPaused ? source.failureReason : source.error,
          isPaused: source.isPaused,
          retry: retrySource,
        }
      : null;
  const registryFailure =
    registry.isPaused || registry.isError
      ? {
          failure: registry.isPaused ? registry.failureReason : registry.error,
          isPaused: registry.isPaused,
          retry: retryRegistry,
        }
      : null;
  // Paused is tested first inside each arm, for the reason `readState` states in
  // `NewPathwayDialog`: a held retry keeps `isPending` true with `isError`
  // false, so testing pending first puts a permanent skeleton over a failure the
  // Editor Service already sent.
  const readFailure = catalogFailure ?? sourceFailure ?? registryFailure;
  const reading =
    readFailure === null &&
    (catalog.isPending ||
      registry.isPending ||
      (sourceAsset !== null && source.isPending));

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) {
          onClose();
        }
      }}
    >
      <DialogContent
        title={copyFrom ? "Create editable copy" : "New domain"}
        description={
          copyFrom
            ? "Creates your own background domain starting from this one. The original stays exactly as it is."
            : "A background domain: one independently versioned set of intake prompts a project pins on its own. Choose an existing domain as exact starting content; the prompts are edited afterwards on the domain's own page."
        }
        busy={busy}
        footer={
          <>
            <Button variant="outline" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              disabled={
                busy ||
                reading ||
                readFailure !== null ||
                // STATED LOCALLY as well as composed above. `reading` and
                // `readFailure` already withhold the whole form while the
                // policy is unread, and this is the invariant that must survive
                // somebody rearranging that composition: nothing is created
                // without a served placement policy.
                !registry.isSuccess ||
                policyUnusable ||
                name.trim() === "" ||
                variant.trim() === "" ||
                domainId.trim() === "" ||
                !variantValid ||
                !domainValid ||
                sourceId === "" ||
                sourceUnavailable
              }
              onClick={submit}
            >
              {copyFrom ? "Create copy" : "Create domain"}
            </Button>
          </>
        }
      >
        {readFailure ? (
          <NamedReadFailureState
            failure={readFailure.failure}
            isPaused={readFailure.isPaused}
            retry={readFailure.retry}
            subject="Library"
            title="This domain could not be prepared"
            description="The Editor Service did not return the library content and placement policy this dialog needs, so nothing can be created yet."
            deniedDescription="You are not authorized to read the shared clinical library."
            offlineDescription="The shared clinical library could not be reached. It loads on its own when the connection returns."
          />
        ) : reading ? (
          <LoadingState label="Loading starting content and placement policy" rows={3} />
        ) : (
          <div className="space-y-4">
            {policyUnusable ? (
              <Notice role="alert" tone="destructive">
                The Editor Service does not describe a background domain&apos;s
                pack placement in a shape this version can assemble, so no
                domain can be created here. Nothing has been changed.
              </Notice>
            ) : null}

            <Field
              label="Name"
              description="What this domain is called in the Clinical library. The prompts are authored afterwards on the domain's own page."
            >
              {({ inputId, describedBy }) => (
                <>
                  <Input
                    id={inputId}
                    value={name}
                    maxLength={NAME_MAX_LENGTH}
                    placeholder="e.g. Military service"
                    aria-invalid={fieldErrors.name !== undefined || undefined}
                    aria-describedby={joinDescriptionIds(
                      describedBy,
                      fieldErrors.name ? nameErrorId : undefined,
                    )}
                    onChange={(event) => setName(event.target.value)}
                  />
                  {fieldErrors.name ? (
                    <FieldError id={nameErrorId} message={fieldErrors.name} />
                  ) : null}
                </>
              )}
            </Field>

            <Field
              label="Domain ID"
              description="The runtime identity of this domain, and the name of the file it lands at in the pack. It is immutable once created, so every project pinning this domain keeps resolving it."
            >
              {({ inputId, describedBy }) => (
                <>
                  <Input
                    id={inputId}
                    value={domainId}
                    dir="ltr"
                    placeholder="e.g. military_service"
                    aria-invalid={
                      (!domainValid && domainId !== "") || undefined
                    }
                    aria-describedby={joinDescriptionIds(
                      describedBy,
                      !domainValid && domainId !== ""
                        ? domainErrorId
                        : undefined,
                    )}
                    onChange={(event) => changeDomainId(event.target.value)}
                  />
                  {!domainValid && domainId !== "" ? (
                    <FieldError
                      id={domainErrorId}
                      message="The Editor Service does not accept this as a pack placement segment."
                    />
                  ) : null}
                </>
              )}
            </Field>

            <Field
              label="Profile variant"
              description="Which BG profile's domain set this belongs to. Every domain a project pins has to resolve in the same profile variant."
            >
              {({ inputId, describedBy }) => (
                <>
                  <Input
                    id={inputId}
                    value={variant}
                    dir="ltr"
                    placeholder="e.g. mod"
                    aria-invalid={(!variantValid && variant !== "") || undefined}
                    aria-describedby={joinDescriptionIds(
                      describedBy,
                      !variantValid && variant !== ""
                        ? variantErrorId
                        : undefined,
                    )}
                    onChange={(event) => setVariant(event.target.value)}
                  />
                  {!variantValid && variant !== "" ? (
                    <FieldError
                      id={variantErrorId}
                      message="The Editor Service does not accept this as a pack placement segment."
                    />
                  ) : null}
                </>
              )}
            </Field>

            {/* The assembled coordinate, shown before it is submitted. The
                clinician typed both halves; seeing the value the service will
                store is what makes an "already taken" refusal legible. */}
            {variant !== "" && domainId !== "" ? (
              <p className="text-xs text-muted-foreground">
                Pack placement{" "}
                <code
                  dir="ltr"
                  className="rounded bg-muted px-1 py-0.5 font-mono text-foreground"
                >
                  {coordinate}
                </code>
              </p>
            ) : null}
            {fieldErrors.placementCoordinate ? (
              <FieldError
                id={placementErrorId}
                message={fieldErrors.placementCoordinate}
              />
            ) : null}

            {copyFrom ? null : (
              <Field
                label="Starting content"
                description="The exact immutable version this domain's v1 copies — its prompts, conditions and extraction settings. Edit them afterwards."
              >
                {({ inputId, describedBy }) => (
                  <Select
                    id={inputId}
                    aria-describedby={describedBy}
                    value={sourceId}
                    onChange={(event) => setSourceId(event.target.value)}
                  >
                    <option value="">Choose a domain…</option>
                    {sourceCandidates.map((candidate) => (
                      <option key={candidate.id} value={candidate.id}>
                        {candidate.name} · {candidate.latestVersion}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            )}

            {sourceUnavailable ? (
              <Notice role="alert" tone="destructive">
                The chosen starting content is no longer in the active library.
                Choose another domain.
              </Notice>
            ) : null}

            <SourcePreview source={source} />

            {generalMessage ? (
              <Notice role="alert" tone="destructive">
                {generalMessage}
              </Notice>
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * What the chosen starting content actually holds, before it becomes v1.
 *
 * A COUNT, never the clinical text: the point is that a clinician creating a
 * domain knows they are inheriting eight real prompts rather than an empty
 * form, which is the difference between this flow and the placeholder v1 the
 * generic dialog would have published.
 */
function SourcePreview({
  source,
}: {
  source: { isSuccess: boolean; data?: { content: unknown } | undefined };
}) {
  if (!source.isSuccess || source.data === undefined) {
    return null;
  }
  const summary = storedDomainSummary(source.data.content);
  if (summary === null) {
    return null;
  }
  return (
    <p className="text-xs text-muted-foreground">
      Starting from {summary.itemCount}{" "}
      {summary.itemCount === 1 ? "prompt" : "prompts"}
      {summary.order === null
        ? ""
        : ` at interview position ${summary.order}`}
      . Every condition, extraction note and quality example is copied with them.
    </p>
  );
}
